import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { PlayIcon, PauseIcon } from 'lucide-react';
interface TeleprompterProps {
  script: string;
  isScrolling: boolean;
  scrollSpeed: number;
  fontSize: number;
  toggleScrolling: () => void;
  highlightColor?: string;
}
export interface TeleprompterHandle {
  scrollUp: (amount?: number) => void;
  scrollDown: (amount?: number) => void;
}
const Teleprompter = forwardRef<TeleprompterHandle, TeleprompterProps>(({
  script,
  isScrolling,
  scrollSpeed,
  fontSize,
  toggleScrolling,
  highlightColor = 'rgba(2, 25, 128, 0.2)'
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonPosition, setButtonPosition] = useState({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    let animationFrameId: number;
    let lastTime = 0;
    const scroll = (timestamp: number) => {
      if (!containerRef.current || !isScrolling) return;
      if (!lastTime) lastTime = timestamp;
      const elapsed = timestamp - lastTime;
      if (elapsed > 16) {
        // Roughly 60fps
        containerRef.current.scrollTop += scrollSpeed * 0.5;
        lastTime = timestamp;
      }
      animationFrameId = requestAnimationFrame(scroll);
    };
    if (isScrolling) {
      animationFrameId = requestAnimationFrame(scroll);
    }
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isScrolling, scrollSpeed]);
  useImperativeHandle(ref, () => ({
    scrollUp: (amount: number = 100) => {
      if (containerRef.current) {
        containerRef.current.scrollTop -= amount;
      }
    },
    scrollDown: (amount: number = 100) => {
      if (containerRef.current) {
        containerRef.current.scrollTop += amount;
      }
    }
  }));
  // Handle button dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setButtonPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseUp = () => setIsDragging(false);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isDragging]);
  // Center the button horizontally by default
  useEffect(() => {
    if (containerRef.current && buttonPosition.x === 0 && buttonPosition.y === 0) {
      const containerWidth = containerRef.current.offsetWidth;
      setButtonPosition({
        x: containerWidth / 2 - 20,
        y: window.innerHeight - 120 // Position from bottom
      });
    }
  }, [containerRef.current]);
  return <div className="flex-1 flex flex-col relative overflow-hidden bg-black pt-16 pb-16" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} data-id="element-50">
        <div className="absolute left-0 right-0 h-1/3 top-1/3 z-10" style={{
      backgroundColor: highlightColor
    }} data-id="element-51"></div>
        <div ref={containerRef} className="flex-1 overflow-y-auto py-[33vh] px-8 scrollbar-hide no-scrollbar bg-black" style={{
      fontSize: `${fontSize}px`,
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-52">
          {script.split('\n').map((paragraph, index) => <p key={index} className="mb-6 text-center" data-id="element-53">
              {paragraph}
            </p>)}
        </div>
        {/* Floating Play/Pause Button */}
        <button className="absolute bg-gray-900 bg-opacity-70 text-[#51a7fa] hover:text-white rounded-full p-2 cursor-move" style={{
      left: `${buttonPosition.x}px`,
      top: `${buttonPosition.y}px`,
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 20
    }} onClick={toggleScrolling} onMouseDown={handleMouseDown} data-id="element-54">
          {isScrolling ? <PauseIcon size={20} data-id="element-55" /> : <PlayIcon size={20} data-id="element-56" />}
        </button>
      </div>;
});
export default Teleprompter;